import { useEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Link from 'next/link'
import Menu from './Menu'
import { useTranslation } from '~/components/TranslationContext'
import {
  Logo,
  Button,
  Avatar,
  space,
  color,
  fontSize,
  device,
  transition,
  easing,
  radius,
  shadow,
  useKeyPress,
  fontWeight,
  useTransition,
  useOnClickOutside,
  Pill,
} from '@ticketswap/solar'
import Overlay from './Overlay'
import AuthDialog from '~/components/AuthDialog'
import SearchOverlay from '~/components/SearchOverlay'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import { AUTHENTICATION_VIEWED, SELL_STARTED } from '~/lib/tracking-events'
import { track } from '~/utils/analytics'
import { MagnifyingGlassAlt, CloseAlt, ListAlt, ChevronDown } from '@ticketswap/solar/icons'
import BecomePartnerButton from '~/components/BecomePartnerButton'
import PropTypes from '~/lib/propTypes'
import { useRouter } from 'next/router'
import SellTicketsButton from '~/components/SellTicketsButton'
import useStats from '~/hooks/useStats'
import { RouteMap } from '~/lib/routeMap'
import { useQuery } from '@apollo/client'
import { GET_LOGGED_IN_USER_KYC_STATUS } from '~/graphql/queries/getLoggedInUserKYCStatus'
import { useIntercom } from '~/components/IntercomProvider'

const duration = 200

export const MenubarContainer = styled.div`
  position: absolute;
  inset-inline-start: 0;
  inset-block-start: 0;
  width: 100%;
`

const StyledLogo = styled(Logo)`
  color: ${props =>
    props.logo === 'blue' || props.showNavOverlay
      ? color.earth
      : props.logo === 'black'
      ? '#000000'
      : color.lightForeground};
`

const Container = styled.div`
  position: relative;
  color: ${color.lightForeground};
  padding-block: ${space[16]};
  padding-inline: ${space[16]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1;

  ${props =>
    props.backgroundImageUrl &&
    css`
      background-color: ${color.darkBackground};

      &::before {
        content: '';
        opacity: 0.25;
        inset: 0;
        position: absolute;
        background-image: url(${props.backgroundImageUrl});
        background-size: cover;
        background-position: center;

        [data-theme='dark'] & {
          background-image: unset;
          opacity: 1;
        }
      }
    `};

  @media ${device.tablet} {
    position: relative;
  }

  @media ${device.tablet} {
    padding-block: ${space[24]};
    padding-inline: ${space[32]};

    &::after {
      display: none;
    }
  }
`

const Nav = styled.nav`
  position: relative;
`

const NavList = styled.ul`
  position: relative;
  display: flex;
  align-items: center;
`

const NavItem = styled.li`
  display: ${props => props.display};

  margin-inline-start: ${space[16]};

  @media ${device.tablet} {
    display: ${props => props.displayMd};

    margin-inline-start: ${space[32]};
  }

  &:first-of-type {
    margin-inline-start: 0;
  }
`

const HomeLink = styled.a`
  line-height: 0;
  position: relative;
  outline: 0;
  transition: color ${transition};
  z-index: 1;

  .light & {
    color: ${color.lightForeground};
  }
  .dark &,
  .light.has-overlay & {
    color: ${color.earth};
  }

  @media ${device.tablet} {
    .light & {
      color: ${color.lightForeground} !important;
    }

    .dark & {
      color: ${color.earth};
    }
  }
`

const NavLink = styled.a`
  color: ${props => (props.theme === 'light' ? 'white' : color.foregroundSubtle)};
  font-size: ${fontSize[18]};
  font-weight: ${fontWeight.semiBold};
  transition: color ${transition};
  outline: 0;
  position: relative;
  z-index: 1;

  .light & {
    color: ${color.lightForeground};
  }
  .dark &,
  .light.has-overlay & {
    color: ${color.foregroundSubtle};
  }

  &:focus {
    box-shadow: none;
  }

  @media ${device.tablet} {
    .light & {
      color: ${color.lightForeground} !important;
    }

    .dark & {
      color: ${color.foregroundSubtle};
    }
  }
`

const MenuDropdownWrapper = styled.div`
  position: relative;
`

const MenuDropdownContainer = styled.div`
  opacity: ${props => (props.state === 'ENTERING' || props.state === 'ENTERED' ? 1 : 0)};
  transform: ${props => {
    switch (props.state) {
      case 'EXITED':
        return 'translate3d(0,-1rem,0) scale(0.5)'
      case 'EXITING':
        return 'translate3d(0,0rem,0) scale(1)'
      default:
        return 'translate3d(0,0,0) scale(1)'
    }
  }};
  transition-duration: ${duration}ms;
  transition-timing-function: ${easing.easeOutCubic};
  transition-property: opacity, transform;
`

const MenuDropdown = styled.ul`
  position: absolute;
  padding-block: ${space[16]};
  padding-inline: ${space[16]};
  inset-inline-start: 50%;
  inset-block-start: calc(100% ${props => (props.distance ? ` + ${space[props.distance]}` : null)});
  transform: translateX(-50%);
  border-radius: ${radius.lg};
  display: block;
  box-shadow: ${shadow.strong};
  background-color: ${color.background};
  color: ${color.foreground};

  [data-theme='dark'] & {
    border: 1px solid ${color.stroke};
  }
`

const LittleArrow = styled.span`
  display: inline-block;
  margin-inline-start: ${space[4]};

  .light & {
    color: ${color.lightForeground};
  }
  .dark &,
  .light.has-overlay & {
    color: ${color.foregroundSubtle};
  }

  @media ${device.tablet} {
    .light & {
      color: ${color.lightForeground} !important;
    }
    .dark & {
      color: ${color.foregroundSubtle};
    }
  }
`

const Attention = styled(Pill)`
  position: absolute;
  inset-block-start: 30px;
  inset-inline-start: 30px;
`

const BrandButton = styled(Button)`
  background-color: ${color.brand};
  color: ${color.onBrand};
`

const Offers = styled.span`
  background-color: ${color.earth};
  position: absolute;
  inset-inline-end: -${space[12]};
  font-size: 8px;
  border-radius: ${space[8]};
  font-weight: ${fontWeight.bold};
  inset-block-start: -${space[8]};
  width: ${space[16]};
  height: ${space[16]};
  display: flex;
  justify-content: center;
  align-items: center;
  padding-block-start: 1px;
`

const Menubar = ({
  appearance = 'dark',
  hasSearchToggle = true,
  type = 'default',
  logo = color.lightForeground,
  eventId = '',
  showJobOfferings = false,
  showPartnersButton = false,
  setMenuShown = () => null,
  backgroundImageUrl,
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { show: showIntercom } = useIntercom()
  const { pathname = '' } = router || {}
  const [showNavOverlay, setShowNavOverlay] = useState(false)
  const [showNavMenu, setShowNavMenu] = useState(false)
  const [avatarClicked, setAvatarClicked] = useState(0)
  const { loggedInUser } = useLoggedInUser()
  const { notificationInboxMessages = {} } = loggedInUser || {}
  const { unreadMessageCount = 0 } = notificationInboxMessages

  const { data = {} } = useQuery(GET_LOGGED_IN_USER_KYC_STATUS)
  const { loggedInUser: kycUser } = data
  const { knowYourCustomer } = kycUser || {}
  const needsVerification = knowYourCustomer?.status === 'ACTION_REQUIRED'

  const [state, mounted, transitioning] = useTransition({
    in: showNavMenu,
    timeout: duration,
  })
  const esc = useKeyPress('Escape')
  const ref = useRef()
  useOnClickOutside(ref, () => setShowNavMenu(false))
  const { jobOffers } = useStats({ t })

  useEffect(() => {
    // Close menu with Esc key
    if (showNavMenu && esc && !transitioning) setShowNavMenu(false)
  }, [showNavMenu, esc, transitioning])

  const toggleNavOverlay = () => {
    setMenuShown(!showNavOverlay)
    return setShowNavOverlay(!showNavOverlay)
  }

  const isLoggedIn = Boolean(loggedInUser)

  return (
    <Container
      backgroundImageUrl={backgroundImageUrl}
      className={`${appearance}${showNavOverlay ? ' has-overlay' : ''}`}
    >
      <Link prefetch={false} href="/" passHref>
        <HomeLink aria-label={t('common:menubar.home')}>
          <StyledLogo logo={logo} showNavOverlay={showNavOverlay} />
        </HomeLink>
      </Link>

      <Nav>
        <NavList>
          {hasSearchToggle && (
            <NavItem>
              <SearchOverlay>
                {getToggleProps => (
                  <NavLink as="button" {...getToggleProps}>
                    <MagnifyingGlassAlt />
                  </NavLink>
                )}
              </SearchOverlay>
            </NavItem>
          )}

          {showJobOfferings && (
            <NavItem display="none" displayMd="block">
              <NavLink
                aria-label={t('common:menubar.cart')}
                href="https://jobs.ticketswap.com?utm_source=ticketswap&utm_medium=internal&utm_campaign=header_promotion"
              >
                {t('common:jobs.small')}

                {jobOffers > 9 && <Offers>{jobOffers}</Offers>}
              </NavLink>
            </NavItem>
          )}

          {showPartnersButton && (
            <NavItem display="none" displayMd="block">
              <Link prefetch={false} href={{ pathname: RouteMap['PARTNERS'] }} passHref>
                <NavLink aria-label={t('common:footer.primary.partners')}>
                  {t('common:footer.primary.partners')}
                </NavLink>
              </Link>
            </NavItem>
          )}

          <>
            <NavItem display="block" displayMd="none">
              <NavLink
                as="button"
                onClick={toggleNavOverlay}
                data-testid="menu-overlay-toggle"
                aria-label={t('common:menubar.menuOverlay')}
              >
                {showNavOverlay ? <CloseAlt /> : <ListAlt />}
              </NavLink>
            </NavItem>
            {type !== 'partner' ? (
              <>
                {!isLoggedIn && (
                  <NavItem display="none" displayMd="block">
                    <Link prefetch={false} href={RouteMap['HOW_DOES_IT_WORK']} passHref>
                      <NavLink>{t('common:menubar.howItWorks')}</NavLink>
                    </Link>
                  </NavItem>
                )}

                {isLoggedIn && (
                  <>
                    <NavItem display="none" displayMd="block">
                      <Link prefetch={false} href={{ pathname: RouteMap['CART'] }} passHref>
                        <NavLink>{t('common:menubar.cart')}</NavLink>
                      </Link>
                    </NavItem>

                    <NavItem display="none" displayMd="block">
                      <NavLink as="button" onClick={showIntercom}>
                        {t('common:menubar.help')}
                      </NavLink>
                    </NavItem>
                  </>
                )}
              </>
            ) : (
              <>
                <NavItem display="none" displayMd="block">
                  <Link prefetch={false} href={RouteMap['BLOG']} passHref>
                    <NavLink>{t('common:generic.blog')}</NavLink>
                  </Link>
                </NavItem>

                <NavItem display="none" displayMd="block">
                  <Link prefetch={false} href="/how-does-it-work" passHref>
                    <NavLink>{t('common:menubar.howItWorks')}</NavLink>
                  </Link>
                </NavItem>
              </>
            )}
            <NavItem display="none" displayMd="block">
              <AuthDialog onToggle={on => on && track(AUTHENTICATION_VIEWED)}>
                {({ getToggleProps }) => (
                  <>
                    {isLoggedIn && (
                      <MenuDropdownWrapper ref={ref}>
                        <NavLink
                          as="button"
                          theme="light"
                          onClick={() => setShowNavMenu(!showNavMenu)}
                        >
                          <Avatar
                            src={loggedInUser.avatar}
                            size={44}
                            onClick={() => setAvatarClicked(avatarClicked + 1)}
                          />
                          <LittleArrow>
                            <ChevronDown size={16} />
                          </LittleArrow>

                          {needsVerification && <Attention variant="danger">!</Attention>}

                          {unreadMessageCount > 0 && (
                            <Attention variant="danger">{unreadMessageCount}</Attention>
                          )}
                        </NavLink>

                        {mounted && (
                          <MenuDropdownContainer state={state}>
                            <MenuDropdown distance={16}>
                              <Menu type={type} />
                            </MenuDropdown>
                          </MenuDropdownContainer>
                        )}
                      </MenuDropdownWrapper>
                    )}

                    {!isLoggedIn && (
                      <>
                        {type === 'static' ? (
                          <NavLink href={RouteMap['LOGIN']}>{t('common:menubar.login')}</NavLink>
                        ) : (
                          <NavLink as="button" {...getToggleProps()}>
                            {t('common:menubar.login')}
                          </NavLink>
                        )}
                      </>
                    )}
                  </>
                )}
              </AuthDialog>
            </NavItem>
          </>

          <NavItem display="none" displayMd="block">
            {type === 'default' && (
              <Link prefetch={false} href="/sell" passHref>
                <Button
                  as="a"
                  variant="success"
                  size="medium"
                  onClick={() => {
                    track(SELL_STARTED, {
                      source: 'menu header',
                    })
                  }}
                >
                  {t('common:menubar.sellTickets')}
                </Button>
              </Link>
            )}

            {type === 'sell' && (
              <SellTicketsButton
                label={t('common:menubar.sellTickets')}
                eventId={eventId}
                size="medium"
              />
            )}

            {type === 'partner' && (
              <BecomePartnerButton
                type="default"
                variant="inverted"
                size="medium"
                t={t}
                pathname={pathname}
              />
            )}

            {type === 'brand' && (
              <Link prefetch={false} href="/sell" passHref>
                <BrandButton
                  as="a"
                  size="medium"
                  onClick={() => {
                    track(SELL_STARTED, {
                      source: 'menu header',
                    })
                  }}
                >
                  {t('common:menubar.sellTickets')}
                </BrandButton>
              </Link>
            )}
          </NavItem>
        </NavList>

        {showNavOverlay && <Overlay t={t} type={type} />}
      </Nav>
    </Container>
  )
}

Menubar.propTypes = {
  appearance: PropTypes.oneOf(['dark', 'light']),
  hasSearchToggle: PropTypes.bool,
  showJobOfferings: PropTypes.bool,
  showPartnersButton: PropTypes.bool,
  type: PropTypes.oneOf(['default', 'partner', 'static', 'brand']),
  logo: PropTypes.oneOf(['blue', 'black', 'white']),
  scroll: PropTypes.func,
  setMenuShown: PropTypes.func,
  eventId: PropTypes.string,
  offers: PropTypes.number,
  backgroundImageUrl: PropTypes.string,
}

export default Menubar
